// React
import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
// Translations
import { withTranslation } from "react-i18next";
// Styles
import "components/Accordion/styles/styles.scss";
// Components
import { Button, Input, SelectAutocomplete, SelectSimple } from "@components/Input";
// Assets
import { closeSvg } from "@assets";
// Api
import { IntegrationsAPI } from "api/application/Integrations";
// Helpers
import {
  addEquivalence,
  deleteEquivalence,
  fieldOptions,
  getCentrideskIntegration,
  isMissingField,
  optionsChanged,
  refreshOptions,
  selectField,
  setEquivalenceText,
  setEquivalenceValue,
} from "./centridesk_custom_field_helpers";


const FIELDS_TTL_MS = 60 * 1000;
const fieldsRequests = {};

class CentrideskCustomField extends Component {
  constructor(props) {
    super(props);
    this.state = { fields: null };
  }

  componentDidMount() {
    this._mounted = true;
    this.loadFields();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.integrations !== this.props.integrations && this.state.fields === null) {
      this.loadFields();
    }
  }

  loadFields = () => {
    const { integrations } = this.props;
    const { access, actualProject } = this.props.data;
    const centridesk = getCentrideskIntegration(integrations);
    if (!centridesk) return;

    const cached = fieldsRequests[centridesk.id];
    if (!cached || Date.now() - cached.at > FIELDS_TTL_MS) {
      fieldsRequests[centridesk.id] = {
        at: Date.now(),
        request: new IntegrationsAPI(actualProject.id, access, this.props)
          .custom_fields(centridesk.id)
          .catch(() => {
            delete fieldsRequests[centridesk.id];
            return null;
          }),
      };
    }
    fieldsRequests[centridesk.id].request.then((fields) => {
      if (this._mounted) this.setState({ fields: fields instanceof Array ? fields : null });
    });
  };

  render() {
    const { integrations, mapping, onChange, t } = this.props;
    const closeIcon = this.props.closeIcon || closeSvg;
    const { fields } = this.state;

    if (!getCentrideskIntegration(integrations)) return null;

    const options = fieldOptions(mapping, fields);
    const equivalences = mapping?.equivalences || [];

    return (
      <div className={"accordion__element_use_custom_fields"}>
        {this.props.showLabel && (
          <span className={"accordion__element_use_custom_fields_label"}>
            {t("intents.config.accordion.params.centridesk_custom_field.label")}
          </span>
        )}
        <div className={"actions"}>
          <SelectAutocomplete
            value={mapping?.key_name ? mapping : false}
            searchOption={"title"}
            items={fields || []}
            className={"select_secondary nodrag"}
            onSelect={(field) => onChange(selectField(field))}
            placeholder={t("intents.config.accordion.params.centridesk_custom_field.custom_field")}
            t={t}
          />
          <Button className={"btn_icon"} type={"white"} onClick={() => onChange(null)} text={closeIcon()} />
        </div>

        {isMissingField(mapping, fields) && (
          <span className={"accordion__element_use_custom_fields_warning"}>
            {t("intents.config.accordion.params.centridesk_custom_field.missing")}
          </span>
        )}

        {optionsChanged(mapping, fields) && (
          <span className={"accordion__element_use_custom_fields_warning"}>
            {t("intents.config.accordion.params.centridesk_custom_field.options_changed")}{" "}
            <span
              className={"accordion__element_use_custom_fields_action"}
              onClick={() => onChange(refreshOptions(mapping, fields))}
            >
              {t("intents.config.accordion.params.centridesk_custom_field.update_options")}
            </span>
          </span>
        )}

        {mapping?.type === "checkbox" && (
          <span className={"accordion__element_use_custom_fields_hint"}>
            {t("intents.config.accordion.params.centridesk_custom_field.checkbox_hint")}
          </span>
        )}

        {mapping?.type === "dropdown" && (
          <React.Fragment>
            <Button
              disabled={options.length === 0 || equivalences.length >= options.length}
              type={"dashed"}
              onClick={() => onChange(addEquivalence(mapping))}
              text={t("intents.config.accordion.params.centridesk_custom_field.add_button")}
            />
            {equivalences.map((equivalence, ii) => (
              <div className={"accordion__element_parameter_equivalence"} key={ii}>
                <Input
                  onChange={(e) => onChange(setEquivalenceText(mapping, ii, e.target.value))}
                  value={equivalence.text}
                  placeholder={t("intents.config.accordion.params.centridesk_custom_field.text")}
                />
                <SelectSimple
                  valueSelected={equivalence.value || false}
                  items={options}
                  className={"select_secondary nodrag"}
                  onSelect={(value) => onChange(setEquivalenceValue(mapping, ii, value))}
                  placeholder={t("intents.config.accordion.params.centridesk_custom_field.value")}
                  t={t}
                />
                <div onClick={() => onChange(deleteEquivalence(mapping, ii))} className={"delete_equivalence"}>
                  {closeIcon()}
                </div>
              </div>
            ))}
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ data: state });

CentrideskCustomField.propTypes = {
  integrations: PropTypes.array.isRequired,
  mapping: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  closeIcon: PropTypes.func,
};

export default withTranslation("common")(connect(mapStateToProps)(CentrideskCustomField));
